"use client";

import { useConsent } from "@policystack/react/consent";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "#/components/ui/accordion";
import { Button } from "#/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "#/components/ui/dialog";
import { Switch } from "#/components/ui/switch";
import { useIsClient } from "#/hooks/useIsClient";
import { cn } from "#/lib/utils";

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  essential:
    "These cookies are essential for the website to function properly and cannot be disabled.",
  analytics:
    "These cookies help us understand how visitors interact with the website and improve its performance.",
  marketing:
    "These cookies are used to deliver relevant ads and measure the effectiveness of marketing campaigns.",
};

const ACCORDION_TRIGGER_CLASS =
  "items-center gap-3 px-3 py-3 hover:no-underline **:data-[slot=accordion-trigger-icon]:order-first **:data-[slot=accordion-trigger-icon]:mr-1 **:data-[slot=accordion-trigger-icon]:ml-0";

function CategoryConsentSwitch({
  categoryKey,
  label,
  locked,
  granted,
  onToggle,
}: {
  categoryKey: string;
  label: string;
  locked: boolean;
  granted: boolean;
  onToggle: () => void;
}) {
  return (
    <span
      role="presentation"
      className="ml-auto shrink-0"
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <Switch
        id={`cookie-${categoryKey}`}
        checked={granted}
        onCheckedChange={
          locked
            ? undefined
            : (checked) => {
                if (checked !== granted) onToggle();
              }
        }
        disabled={locked}
        size="sm"
        aria-label={`${label} cookies`}
      />
    </span>
  );
}

function CategoryAccordionItem({
  categoryKey,
  label,
  locked,
  granted,
  onToggle,
}: {
  categoryKey: string;
  label: string;
  locked: boolean;
  granted: boolean;
  onToggle: () => void;
}) {
  return (
    <AccordionItem
      value={categoryKey}
      className="overflow-hidden rounded-lg border border-border not-last:border-b"
    >
      <AccordionTrigger className={ACCORDION_TRIGGER_CLASS}>
        <span className="flex-1 font-medium">{label}</span>
        <CategoryConsentSwitch
          categoryKey={categoryKey}
          label={label}
          locked={locked}
          granted={granted}
          onToggle={onToggle}
        />
      </AccordionTrigger>
      <AccordionContent className="px-3 pb-3">
        <p className="text-sm text-muted-foreground">
          {CATEGORY_DESCRIPTIONS[categoryKey] ?? "Optional cookies for this category."}
        </p>
      </AccordionContent>
    </AccordionItem>
  );
}

function CookiePreferencesDialog() {
  const { route, categories, decisions, toggle, save, setRoute, acceptAll, reject } = useConsent();
  const open = route === "preferences";

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) setRoute("closed");
      }}
    >
      <DialogContent className="sm:max-w-md" showCloseButton>
        <DialogHeader>
          <DialogTitle>Cookie Settings</DialogTitle>
          <DialogDescription>
            Customize your cookie settings here. You can choose which types of cookies and tracking
            technologies you allow.
          </DialogDescription>
        </DialogHeader>
        <Accordion defaultValue={[]} className="flex flex-col gap-2">
          {categories.map((category) => (
            <CategoryAccordionItem
              key={category.key}
              categoryKey={category.key}
              label={category.label}
              locked={category.locked === true}
              granted={decisions[category.key] === true}
              onToggle={() => toggle(category.key)}
            />
          ))}
        </Accordion>
        <DialogFooter className="gap-2 sm:justify-center">
          <Button type="button" variant="outline" onClick={() => acceptAll()}>
            Accept All
          </Button>
          <Button type="button" variant="outline" onClick={() => reject()}>
            Reject All
          </Button>
          <Button type="button" onClick={() => save()}>
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CookieBanner() {
  const { route, acceptAll, acceptNecessary, setRoute, repromptReason } = useConsent();
  const visible = route === "cookie";

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className={cn(
        "fixed inset-x-0 bottom-0 z-50 border-t bg-background/95 p-4 shadow-lg backdrop-blur-md",
        "animate-in fade-in-0 slide-in-from-bottom-4 duration-200",
      )}
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="max-w-2xl space-y-1">
          <p className="font-heading text-base font-medium text-foreground">
            We value your privacy
          </p>
          <p className="text-sm text-muted-foreground">
            This site uses cookies to improve your browsing experience and analyze site traffic.
            {repromptReason ? " Our cookie policy changed — please review your choices." : null}
          </p>
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
          <Button type="button" variant="outline" onClick={() => setRoute("preferences")}>
            Customize
          </Button>
          <Button type="button" variant="outline" onClick={() => acceptNecessary()}>
            Necessary only
          </Button>
          <Button type="button" onClick={() => acceptAll()}>
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
}

export function CookieConsent() {
  const mounted = useIsClient();

  if (!mounted) return null;

  return (
    <>
      <CookieBanner />
      <CookiePreferencesDialog />
    </>
  );
}

export function CookieSettingsButton({ className }: { className?: string }) {
  const { setRoute } = useConsent();

  return (
    <Button
      type="button"
      variant="link"
      className={cn("h-auto p-0 text-muted-foreground", className)}
      onClick={() => setRoute("preferences")}
    >
      Cookie settings
    </Button>
  );
}
